import axios from 'axios'
import { defineStore } from 'pinia'
import { BASE_URL } from '@/environment/environment'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import type { IUsefulLink } from '@/modules/links/types/IUsefulLink'
import type { IUsefulLinksStoreState } from '@/modules/links/types/IUsefulLinksStoreState'
import { logger } from '@/utils/eventLogger'

export const useLinksStore = defineStore('linksStore', {
  state: (): IUsefulLinksStoreState => ({
    nextUsefulLinksCursor: null,
    links: [],
    favoritLinks: [],
    filters: { search: '' },
  }),

  actions: {
    /** Закреплённые ссылки показываются в избранном у всех, независимо от `favorit` */
    syncFavoritLinks() {
      this.favoritLinks = this.links.filter(l => l.favorit || l.is_pinned)
    },

    async fetchUsefulLinks(cursor?: string): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        /* Бэк отдаёт и неактивные ссылки — пользователю показываем только активные */
        const params: Record<string, any> = { is_active: true }
        if (this.filters.search) params.search = this.filters.search
        if (cursor) params.cursor = cursor

        const response = await axios.get(`${BASE_URL}/api/links/useful-link/`, { params })
        const results: IUsefulLink[] = response.data.results ?? response.data
        this.links = cursor ? [...this.links, ...results] : results
        this.nextUsefulLinksCursor = response.data.next ?? null
        this.syncFavoritLinks()
      } catch (error) {
        console.error(`❌ Ошибка загрузки полезных ссылок: ${error}`)
        feedbackStore.showToast({
          type: 'error',
          message: 'Не удалось загрузить полезные ссылки',
          time: 7000,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async toggleFavorite(id: string) {
      const feedbackStore = useFeedbackStore()

      const idx = this.links.findIndex(l => l.id === id)
      if (idx === -1) return

      const prev = this.links[idx].favorit
      this.links[idx].favorit = !prev
      this.syncFavoritLinks()

      try {
        await axios.post(`${BASE_URL}/api/links/useful-link/${id}/toggle-favorite/`)
      } catch (error) {
        logger.error('toggleFavorite_error', {
          store: 'linksStore',
          function: 'toggleFavorite',
          error: `❌ Ошибка загрузки полезных ссылок: ${error}`,
          payload: `${id}`,
        })
        feedbackStore.showToast({
          type: 'error',
          message: `${error}`,
          time: 7000,
        })
      }
    },

    resetFilters() {
      this.filters.search = ''
      void this.fetchUsefulLinks()
    },
  },
})
